import React, { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import monthlyVacationService from '../../services/api/monthlyVacationService';
import { monthlyVacationCopy } from './monthlyVacationCopy';

const Box = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin: 0.75rem 0;
  label { display: flex; align-items: center; gap: 0.6rem; min-height: 36px; cursor: pointer; }
  input { width: 18px; height: 18px; flex-shrink: 0; }
  small { display: block; margin: 0.2rem 0 0 1.75rem; color: ${({ theme }) => theme.colors.medium}; }
  [role='alert'] { color: ${({ theme }) => theme.colors.error}; }
`;

const MonthDeclaration = ({ year, month }) => {
  const { i18n } = useTranslation();
  const copy = monthlyVacationCopy(i18n.language);
  const hintId = useId();
  const [taken, setTaken] = useState(false);
  const [declared, setDeclared] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    let current = true;
    setLoading(true); setError(null); setSaved(false);
    monthlyVacationService.get(year, month)
      .then((result) => { if (current) { setTaken(result.vacationTaken === true); setDeclared(result.vacationDeclared === true); } })
      .catch(() => { if (current) setError('load'); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [year, month, attempt]);

  const change = async (event) => {
    const next = event.target.checked;
    setSaving(true); setSaved(false); setError(null);
    try {
      const result = await monthlyVacationService.save(year, month, next);
      if (mounted.current) { setTaken(result.vacationTaken === true); setDeclared(result.vacationDeclared === true); setSaved(true); }
    } catch (_) {
      if (mounted.current) setError('save');
    } finally {
      if (mounted.current) setSaving(false);
    }
  };
  const monthLabel = new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric' });
  return (
    <Box>
      <label>
        <input type="checkbox" checked={taken} onChange={change} disabled={loading || saving || error === 'load'} aria-describedby={hintId} />
        <span>{copy.question} <strong>{monthLabel}</strong></span>
      </label>
      <small id={hintId}>{copy.hint}</small>
      <small role="status" aria-live="polite">{loading ? copy.loading : saving ? copy.saving : saved ? copy.saved : !declared && !error ? copy.unreported : ''}</small>
      {error && <small role="alert">{error === 'load' ? copy.loadError : copy.saveError}{' '}
        {error === 'load' && <button type="button" onClick={() => setAttempt((value) => value + 1)}>{copy.retry}</button>}
      </small>}
    </Box>
  );
};

// A keyed instance prevents a delayed save/load for the previous month from
// changing the checkbox for the newly displayed month.
const MonthlyVacationDeclaration = ({ year, month }) => {
  if (!Number.isInteger(year) || year < 2000 || year > 2100 || !Number.isInteger(month) || month < 1 || month > 12) return null;
  return <MonthDeclaration key={`${year}-${month}`} year={year} month={month} />;
};

export const vacationMonthsForDates = (dates) => [...new Set(dates.filter(Boolean).map((date) => date.slice(0, 7)))].sort()
  .map((period) => ({ year: Number(period.slice(0, 4)), month: Number(period.slice(5, 7)) }));

export default MonthlyVacationDeclaration;
